import React, { useCallback, useMemo, useState } from "react";
import {
  TickerChart,
  formatTimestampLabel,
  type TickerChartDataPoint,
  type TickerChartData,
  type TickerChartTimeInterval,
} from "@bloomberg/media-ui-markets";
import styles from "./styles.module.css";
import {
  ComparisonChartPlaceholder,
  LegendsPlaceholder,
} from "~components/BenchmarkComparison/BenchmarkComparisonPlaceholder";
import TickerChartLegend from "~components/TickerChartLegend";
import type { TickerChartLegendData } from "~types/types";
import { PlaceholderWithError } from "~components/PlaceholderWithError";
import { logError } from "~utils/newrelic/newrelicBrowser";
import { useOnDesktop } from "~hooks/useScreenBreakpoints";
import { useLocale } from "~hooks/i18n/useLocale";
import { Locale } from "~constants/i18n";

const TIMEZONE_ASIA_TOKYO = "Asia/Tokyo";
const JA_HOUR_MINUTE_FORMATTER = new Intl.DateTimeFormat(Locale.Ja, {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TIMEZONE_ASIA_TOKYO,
});

const JA_SHORT_MONTH_DAY_FORMATTER = new Intl.DateTimeFormat(Locale.Ja, {
  month: "short",
  day: "2-digit",
  timeZone: TIMEZONE_ASIA_TOKYO,
});

const JA_MONTH_YEAR_DAY_FORMATTER = new Intl.DateTimeFormat(Locale.Ja, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  timeZone: TIMEZONE_ASIA_TOKYO,
});

type ComparisonChartProp = {
  selectedTab: string;
  tickersArr: Array<string>;
  tickerChartData: Array<TickerChartData>;
  isLoading: boolean;
  isError: boolean;
  refetchData: () => void;
  tickerLegends: Array<TickerChartLegendData>;
  handleTickerSelectionChange: (tickerId: string, isChecked: boolean) => void;
  handleTickerRemoval: (itemId: string) => void;
};

const LEFT_RAIL_CHART_WIDTH = 353;
const TABLET_CHART_WIDTH = 618;
const CHART_HEIGHT = 220;

export function ChartLegendSection({
  isLoading = false,
  isError = false,
  refetchData,
  tickerChartData,
  selectedTab,
  tickersArr,
  tickerLegends,
  handleTickerRemoval,
  handleTickerSelectionChange,
}: ComparisonChartProp) {
  const [hoveredChartData, setHoveredChartData] = useState<
    TickerChartDataPoint | undefined
  >(undefined);

  const { locale } = useLocale();
  const onDesktop = useOnDesktop();

  const { chartWidth, legendPlaceholderRows } = useMemo(
    () => ({
      chartWidth: onDesktop ? LEFT_RAIL_CHART_WIDTH : TABLET_CHART_WIDTH,
      legendPlaceholderRows: onDesktop ? tickersArr.length : 1,
    }),
    [onDesktop, tickersArr]
  );

  const isSingleTicker = tickerChartData.length === 1;

  const xAxisInterval = useMemo(() => {
    if (selectedTab === "5Y") {
      return "month";
    }

    return selectedTab === "1D" ? "hour" : "day";
  }, [selectedTab]);

  const chartData = useMemo(() => {
    if (tickerChartData.length === 1) {
      return [{ ...tickerChartData[0], color: "black" }];
    }

    return tickerChartData;
  }, [tickerChartData]);

  const formatXAxisLabel = useCallback(
    (
      timestamp: number,
      intervalMode: TickerChartTimeInterval,
      isHighlight = false
    ) => {
      if (locale === Locale.Ja && isHighlight) {
        let xLabel;

        switch (intervalMode) {
          case "hour":
            xLabel = JA_HOUR_MINUTE_FORMATTER.format(new Date(timestamp));
            break;
          case "month":
            xLabel = JA_MONTH_YEAR_DAY_FORMATTER.format(new Date(timestamp));
            break;
          default:
            xLabel = JA_SHORT_MONTH_DAY_FORMATTER.format(new Date(timestamp));
        }

        return xLabel;
      }

      return formatTimestampLabel(timestamp, intervalMode, isHighlight);
    },
    [locale]
  );

  if (isLoading) {
    return (
      <>
        <ComparisonChartPlaceholder />
        <LegendsPlaceholder rows={legendPlaceholderRows} />
      </>
    );
  }

  if (isError) {
    logError(new Error("CompareBenchmarks fetch error"), {
      selectedTimeFrame: selectedTab,
      tickers: tickersArr.join(","),
    });

    return (
      <PlaceholderWithError
        errorMessage={"There was an error loading market data."}
        placeholder={<ComparisonChartPlaceholder />}
        onRetryClick={refetchData}
      />
    );
  }

  return (
    <div>
      <TickerChart
        tickerSnapshots={chartData}
        renderMode={isSingleTicker ? "price" : "percentage"}
        showGradient={isSingleTicker}
        pricePrecision={2}
        enableDragInteraction={false}
        xAxisInterval={xAxisInterval}
        xAxisTickSize={4}
        yAxisTickSize={4}
        handleChartHoverInteraction={setHoveredChartData}
        width={chartWidth}
        height={CHART_HEIGHT}
        formatXAxisLabel={formatXAxisLabel}
      />

      <TickerChartLegend
        className={styles.tickerChartLegend}
        allowFirstTickerRemoval={true}
        enableTickerRemoval
        hoveredDataPoint={hoveredChartData}
        tickerLegendData={tickerLegends}
        handleTickerSelectionChange={handleTickerSelectionChange}
        handleTickerRemoval={handleTickerRemoval}
      />
    </div>
  );
}
